import { t } from 'i18n';

/**
 * 从 GraphQL 错误中提取后端返回的 i18n messageKey。
 */
export function extractGraphQLMessageKey(e: unknown): string | undefined {
    return (e as any)?.graphQLErrors?.[0]?.extensions?.messageKey as string | undefined;
}

/**
 * 将 GraphQL 错误翻译为本地化文案，无 messageKey 时使用 fallbackKey 兜底。
 *
 * messageKey 存在但没有对应 i18n 文案时（后端先上、前端文案未补的常见情况），
 * t() 会原样返回 key，直接展示会让用户看到 `errors.business.xxx` 这种内容，
 * 因此这种情况同样回落到 fallbackKey。
 */
export function translateGraphQLError(e: unknown, fallbackKey: string): string {
    const messageKey = extractGraphQLMessageKey(e);
    if (!messageKey) {
        return t(fallbackKey);
    }
    const translated = t(messageKey);
    return translated === messageKey ? t(fallbackKey) : translated;
}

type MessageKeyResult = { message?: string | null; messageKey?: string | null } | null | undefined;

/**
 * 解析结算域转发接口（新服务转发路径）的失败展示文案：
 * 有 messageKey 就按 domain 对应的 i18n 前缀查字典翻译，没有（老路径，老路径失败态一般走抛异常，
 * 见 workflowErrorMessage.ts）就展示原有 message。
 */
function resolveMessageByPrefix(prefix: string, res: MessageKeyResult, fallback: string | null): string {
    if (!res) return fallback ?? '';
    if (res.messageKey) return t(`${prefix}${res.messageKey}`);
    return res.message ?? fallback ?? '';
}

const PRICING_SCHEME_MESSAGE_KEY_PREFIX = 'errors.business.pricingScheme.';

export function resolvePricingSchemeMessage(res?: MessageKeyResult, fallback: string | null = ''): string {
    return resolveMessageByPrefix(PRICING_SCHEME_MESSAGE_KEY_PREFIX, res, fallback);
}

const WORKFLOW_MESSAGE_KEY_PREFIX = 'errors.business.workflow.';

export function resolveWorkflowMessage(res?: MessageKeyResult, fallback: string | null = ''): string {
    return resolveMessageByPrefix(WORKFLOW_MESSAGE_KEY_PREFIX, res, fallback);
}
